// src/components/auth/AuthGate.tsx
// Protects routes — requires authentication

import { ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'

export interface AuthGateProps {
  children: ReactNode
  requireRole?: 'customer' | 'vendor_member' | 'super_admin' | 'any'
  redirectTo?: string
}

export function AuthGate({
  children,
  requireRole = 'any',
  redirectTo = '/auth/login'
}: AuthGateProps) {
  const { loading, user, account } = useAuth()
  const location = useLocation()

  // Two distinct failure modes leave AuthGate spinning:
  //   1. supabase.auth.getSession() hangs        → user stays null
  //   2. session is fine, fetchAccount stalls    → user is set but
  //      account is null indefinitely (RLS edge case, dropped TCP,
  //      slow Maldives 4G that just dies)
  // The previous fix only handled (1). The previous version's
  // `bailoutTimedOut && !user` check NEVER fired for (2) because
  // user IS set in that scenario, so users hit a permanent "Loading…"
  // and had to manually navigate to / to escape.
  //
  // New rule: we are "still waiting" if either useAuth is loading OR
  // we have a session but no account row yet. A single timer covers
  // both shapes. After 15s of either, we redirect to login — better
  // to ask the user to re-authenticate than leave them stuck.
  const stillWaiting = loading || (!!user && !account)

  const [bailoutTimedOut, setBailoutTimedOut] = useState(false)
  useEffect(() => {
    if (!stillWaiting) {
      setBailoutTimedOut(false)
      return
    }
    const timer = setTimeout(() => setBailoutTimedOut(true), 15_000)
    return () => clearTimeout(timer)
  }, [stillWaiting])

  if (bailoutTimedOut) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Definitively unauthenticated — useAuth has resolved with no session.
  if (!loading && !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Either still resolving the session or waiting on the account row.
  if (stillWaiting || !account) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-ink-50 text-sm">Loading…</div>
      </div>
    )
  }

  // Check role requirements — super_admin can access any gated route
  if (
    requireRole !== 'any' &&
    account.role !== requireRole &&
    account.role !== 'super_admin'
  ) {
    return <Navigate to="/" replace />
  }
  
  // Vendor must connect Telegram
  if (
    account.role === 'vendor_member' && 
    account.telegram_required && 
    !account.telegram_user_id &&
    location.pathname !== '/auth/connect-telegram'
  ) {
    return <Navigate to="/auth/connect-telegram" replace />
  }
  
  return <>{children}</>
}
